import React from 'react'
import {
  Box,
  Container,
  Divider,
  Grid,
  Link,
  Typography,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  useTheme,
} from '@mui/material'
import JSONDATA from '../Assets/JSON/MV.json' // Assuming this path is correct
import { styled } from '@mui/system'
import {
  Facebook,
  Twitter,
  LinkedIn,
  Email,
  Phone,
  LocationOn,
  School,
  Description,
  ContactMail,
  Info,
} from '@mui/icons-material'
import CollegeLogo from '../Assets/Images/Header_Logo.png' // Assuming this path is correct

const Footer = () => {
  const theme = useTheme()

  // Styled components for better organization and reusability
  const FooterWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    backgroundColor: theme.palette.background.footerBackground, // Using theme's dark primary for consistency
    padding: theme.spacing(6, 2), // Slightly reduced vertical padding, more balanced
    overflow: 'hidden',
    borderTop: `1px solid ${theme.palette.divider}`,
  }))

  const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.inverse, // Light text for links
    transition: 'all 0.3s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.secondary.main, // A distinct hover color from your theme
    },
  }))

  const SectionTitle = styled(Typography)(({ theme }) => ({
    variant: 'h6',
    fontWeight: 700,
    color: theme.palette.text.inverse, // Use a contrasting color for titles
    marginBottom: theme.spacing(2), // Add some space below titles
  }))

  const socialLinks = [
    { icon: <Facebook />, url: 'https://facebook.com' },
    { icon: <Twitter />, url: 'https://twitter.com' },
    { icon: <LinkedIn />, url: 'https://linkedin.com' },
  ]

  const quickLinks = [
    { label: 'About Us', href: '/home', icon: <Info fontSize="small" /> },
    {
      label: 'Admissions',
      href: '/admissions',
      icon: <School fontSize="small" />,
    },
    {
      label: 'Resources',
      href: '/resources',
      icon: <Description fontSize="small" />,
    },
    {
      label: 'Contact Us',
      href: '/contactus',
      icon: <ContactMail fontSize="small" />,
    },
  ]

  return (
    <FooterWrapper component="footer">
      <Container maxWidth="lg">
        <Grid container spacing={5} justifyContent="space-between">
          {/* Logo and Contact Section */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
              <Box
                component="img"
                src={CollegeLogo}
                alt="College Logo"
                sx={{
                  width: {
                    xs: '70%', // Adjusted for mobile, slightly smaller for better fit
                    sm: '90%',
                    md: '100%',
                  },
                  maxWidth: 500,
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  mb: 1,
                }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color={theme.palette.text.inverse}
              >
                ICET Code: BRAB
              </Typography>

              <List dense disablePadding sx={{ width: '100%' }}>
                <ListItem disableGutters>
                  <ListItemIcon
                    sx={{ minWidth: 32, color: theme.palette.text.inverse }}
                  >
                    <LocationOn fontSize="small" />
                  </ListItemIcon>
                  <Typography
                    variant="body2"
                    color={theme.palette.text.inverse}
                  >
                    Dr. BR Ambedkar Institute of Management and Technology, AP
                    Housing Board, Bagh Lingampally, New Nallakunta, Hyderabad,
                    Telangana-500044.
                  </Typography>
                </ListItem>

                <ListItem disableGutters>
                  <ListItemIcon
                    sx={{ minWidth: 32, color: theme.palette.text.inverse }}
                  >
                    <Email fontSize="small" />
                  </ListItemIcon>
                  <StyledLink href="mailto:principal.mba@dbraei.edu.in">
                    principal.mba@dbraei.edu.in
                  </StyledLink>
                </ListItem>

                <ListItem disableGutters>
                  <ListItemIcon
                    sx={{ minWidth: 32, color: theme.palette.text.inverse }}
                  >
                    <Phone fontSize="small" />
                  </ListItemIcon>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    <StyledLink href="tel:040-27670675">
                      040 – 2767 06 75
                    </StyledLink>{' '}
                    <Typography color={theme.palette.text.inverse}>
                      /
                    </Typography>
                    <StyledLink href="tel:+919100221282">9100221282</StyledLink>{' '}
                    <Typography color={theme.palette.text.inverse}>
                      /
                    </Typography>
                    <StyledLink href="tel:+918331041175">8331041174</StyledLink>
                  </Stack>
                </ListItem>
              </List>
            </Stack>
          </Grid>

          {/* Courses Section */}
          <Grid item xs={12} sm={6} md={3}>
            {' '}
            {/* Adjusted md size for better distribution */}
            <Stack spacing={2}>
              <SectionTitle>Our Courses</SectionTitle>
              <Typography
                variant="body1" // More suitable for this specific course title
                fontWeight={600}
                color={theme.palette.text.inverse}
                mb={1} // Add some margin below the "MBA - 2 Years" title
              >
                MBA - 2 Years
              </Typography>
              <List dense disablePadding>
                {JSONDATA?.MBA_Courses.map((item, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemIcon
                      sx={{
                        minWidth: 32,
                        color: theme.palette.text.inverse,
                      }}
                    >
                      <School fontSize="small" />
                    </ListItemIcon>
                    <Typography
                      variant="body2"
                      color={theme.palette.text.inverse}
                    >
                      {item.title}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={3}>
            {' '}
            {/* Adjusted md size */}
            <Stack spacing={2}>
              <SectionTitle>Quick Links</SectionTitle>
              <List dense disablePadding>
                {quickLinks.map((link, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemIcon
                      sx={{
                        minWidth: 32,
                        color: theme.palette.text.inverse,
                      }}
                    >
                      {link.icon}
                    </ListItemIcon>
                    <StyledLink href={link.href}>{link.label}</StyledLink>
                  </ListItem>
                ))}
              </List>

              <Box mt={3}>
                {' '}
                {/* Increased top margin for separation */}
                <Typography
                  variant="body1"
                  fontWeight={600}
                  color={theme.palette.text.inverse}
                  mb={1}
                >
                  Connect With Us
                </Typography>
                <Stack direction="row" spacing={1}>
                  {' '}
                  {/* Slightly reduced spacing for icons */}
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer" // Added for security best practices
                      aria-label={
                        social.url.split('//')[1].split('.')[0] + ' link'
                      } // Accessibility
                      sx={{
                        color: theme.palette.text.inverse,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                          backgroundColor: 'background.primaryBackground', // A slightly darker secondary for hover
                          color: theme.palette.text.inverse, // White text on hover
                        },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider
          sx={{
            backgroundColor: theme.palette.secondary.light,
            my: 4,
            mx: 'auto',
            width: '100%',
          }}
        />

        {/* Copyright Text */}
        <Typography
          variant="caption" // Smaller font for copyright for less prominence
          color={theme.palette.text.disabled} // Even lighter color for subtle copyright
          textAlign="center"
          sx={{
            fontSize: '0.8rem', // Fine-tune font size
            opacity: 0.7, // Slightly more opaque
          }}
        >
          © {new Date().getFullYear()} Dr. B.R. Ambedkar Institute of Management
          & Technology. All rights reserved.
        </Typography>
      </Container>
    </FooterWrapper>
  )
}

export default Footer
